<template>
  <div class="login-view">
    <div class="form-box">
      <div class="form-row">
        <prefix-code-picker :code="prefixCode" @select="goAreaList" />
        <van-field
          class="wb-form-input"
          type="number"
          v-model="phone"
          :placeholder="$t('login.enterPhone')"
          :border="false"
          @input="phoneChange"
        />
      </div>

      <div class="form-row" :class="{ 'form-row-err': vcodeErr }">
        <van-field
          class="wb-form-input"
          type="number"
          v-model="vcode"
          @input="vcodeChange"
          :placeholder="$t('login.pleaseEnterVcode')"
          :border="false"
          :maxlength="4"
        />
        <vcode-timer-btn
          ref="vcodeBtn"
          :seconds="60"
          :allowClick="canSendVcode"
          :onBeforeSend="clickSend"
          :onSend="sendVcode"
          needBefore
        />
      </div>
      <div class="form-err-tips" v-if="vcodeErr">{{ vcodeErr }}</div>
    </div>

    <div class="read-and-agree" :style="agreementBarStyle">
      <van-checkbox :value="isReadAndAgree" icon-size="14px" @click="isReadAndAgree = !isReadAndAgree">
        <span>{{ $t('login.iAgreed') }}</span>
        <a class="doc-link" href="javascript:;" @click.stop="goLink('provisionStatementUrl')">{{
          `《${$t('login.serviceAgreement')}》`
        }}</a>
        <a class="doc-link" href="javascript:;" @click.stop="goLink('privacyPolicyUrl')">{{
          `《${$t('login.privacyAgreement')}》`
        }}</a>
      </van-checkbox>
    </div>
    <!-- 登录/注册 -->
    <div class="submit-btn" :style="btnBarStyle">
      <wb-primary-btn
        :msg="finalBtnLabel"
        type="primary"
        :disabled="!canSubmit"
        :isLoading="isSubmiting"
        @tap="doSubmit"
      />
    </div>

    <user-lock-dialog :show="showUserLock" @close="showUserLock = false" />

    <!-- 登录注册行为验证码 -->
    <captcha-vertify :show.sync="captchaVisible" type="16" @onPass="captchaPass" />
  </div>
</template>

<script>
  import prefixCodePicker from '@/components/prefix-code-picker.vue'
  import VcodeTimerBtn from '@/components/vcode-timer-btn.vue'
  import WbPrimaryBtn from '@/components/wb-primary-btn.vue'
  import UserLockDialog from '@/components/user-lock-dialog.vue'
  import { getVerifyCode, doLoginByCode, doLoginWithWechat, inviteAndSignIn } from '@/api/api.js'
  import { wbToast } from '@/utils/tips.js'
  import Storage from 'good-storage'
  import { phoneFormatValidate, genUserVerifyState, genDeviceInfo, genDeviceUuid } from '@/utils/user-verify-helper.js'
  import appConfig from '@/config/index.js'
  import langHelper from '@/language/helper.js'
  import LoginHelper from './login-helper'
  import i18n from '@/language'

  import CaptchaVertify from '@/components/captcha/captcha-vertify.vue'

  export default {
    name: 'login-view',

    components: { prefixCodePicker, VcodeTimerBtn, WbPrimaryBtn, UserLockDialog, CaptchaVertify },

    props: {
      // 协议栏样式
      agreementBarStyle: {
        type: Object,
        default: () => {}
      },
      // 按钮栏样式
      btnBarStyle: {
        type: Object,
        default: () => {}
      },
      btnLabel: {
        type: String,
        default: undefined
      },
      // 渠道编号
      channelCode: {
        type: String,
        default: undefined
      }
    },

    data() {
      return {
        captchaVisible: false,
        prefixCode: '+86',
        phone: undefined,
        phoneErr: '',
        vcode: undefined,
        vcodeErr: '',
        sendCodeCount: 0, // 验证码发送次数
        isReadAndAgree: false, // 已同意
        isSubmiting: false, // 是否正在提交中
        showUserLock: false // 是否显示账户锁定弹窗
      }
    },

    computed: {
      finalBtnLabel() {
        return this.btnLabel ? this.btnLabel : i18n.t('login.signIn') + ' / ' + i18n.t('login.signUp')
      },

      canSendVcode() {
        if (!this.phone || this.phoneErr) {
          return false
        }
        return true
      },

      canSubmit() {
        return this.canSendVcode && this.sendCodeCount > 0 && this.vcode && !this.vcodeErr
      }
    },

    mounted() {
      this.initPage()
    },

    methods: {
      initPage() {
        LoginHelper.initLoginParams(this)
        this.prefixCodeChange()
      },

      prefixCodeChange() {
        this.prefixCode = Storage.get('prefixCode') || '+86'
        this.$forceUpdate()
      },

      phoneChange() {
        this.phoneValidate()
      },

      phoneValidate() {
        if (!this.phone) {
          this.phoneErr = this.$t('login.enterPhone') // 请输入手机号
        } else {
          const flag = phoneFormatValidate(this.prefixCode, this.phone)
          if (flag) {
            this.phoneErr = ''
          } else {
            this.phoneErr = this.$t('login.pleaseEnterCorrectPhone') // 请输入正确的手机号
          }
        }
      },

      vcodeChange() {
        this.vcodeErr = ''
      },

      // 点击发送短信验证按钮
      clickSend() {
        this.captchaVisible = true
      },

      captchaPass() {
        if (this.$refs.vcodeBtn) {
          this.$refs.vcodeBtn.doHandle()
        }
      },

      async sendVcode() {
        try {
          const vcode = await genDeviceUuid()
          const captchaCode = `${vcode}_16`
          var { code, msg } = await getVerifyCode(this.prefixCode, this.phone, captchaCode, 16, this.channelCode)
          this.sendCodeCount++
          if (code + '' === '0') {
            wbToast(msg)
            return true
          } else {
            wbToast(msg)
            return false
          }
        } catch (error) {
          return false
        }
      },

      goAreaList() {
        // window.location.href = './area-list.html'
        window.location.replace('./area-list.html')
      },

      goLink(linkKey, isReplace = false) {
        let linkUrl = appConfig.linkDict[linkKey]
        if (linkUrl) {
          let lang = localStorage.getItem(langHelper.localStroageKey)
          if (lang) {
            lang = lang.split('-').join('_')
            linkUrl += `?lang=${lang}`
          }
          if (!isReplace) {
            window.open(linkUrl)
          } else {
            window.location.href = linkUrl
          }
        }
      },

      async doSubmit() {
        if (!this.isReadAndAgree) {
          wbToast(this.$t('login.pleasePickUserStatement')) // 请勾选用户协议
          return
        }
        try {
          this.isSubmiting = true
          const deviceInfo = await genDeviceInfo()
          let params = {
            countryCode: this.prefixCode,
            phone: this.phone,
            code: this.vcode,
            channel: 'H5',
            channelId: LoginHelper.channelId,
            utmSource: LoginHelper.utmSource,
            utmMedium: LoginHelper.utmMedium,
            inviterId: LoginHelper.inviterId,
            ...deviceInfo
          }

          if (LoginHelper.isWechatAuth) {
            // 微信授权登录注册（绑定）
            params['openId'] = LoginHelper.wxOpenId
            params['nickname'] = LoginHelper.wxNickname
            const { code, msg } = await doLoginWithWechat(params)
            LoginHelper.wxBindResHandle(code, msg)
          } else if (LoginHelper.activityUserId) {
            // 邀请绑定活动
            params = LoginHelper.buildInviteActivitySubmitParam(params)
            const { code, data, msg } = await inviteAndSignIn(params)
            this.loginResHandle(code, data, msg)
          } else {
            // 默认、渠道登录、百度推广
            const { code, data, msg } = await doLoginByCode(params)
            this.loginResHandle(code, data, msg)
          }
        } catch (error) {
          wbToast(this.$t('common.reqError'))
        } finally {
          this.isSubmiting = false
        }
      },

      // 登录结果处理
      loginResHandle(code, data, msg) {
        if (code === 0) {
          if (!LoginHelper.activityUserId) {
            wbToast(this.$t('login.signInSuccess')) // 登录成功
          }
          setTimeout(() => {
            LoginHelper.loginSuccessHandle(data)
          }, 800)
        } else {
          const state = genUserVerifyState(code)
          if (state === 3) {
            wbToast(msg)
            this.vcodeErr = msg
          } else if (state === 4) {
            // 账号已锁定，请致电 \n +852 23709538
            this.showUserLock = true
          } else {
            wbToast(msg)
          }
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .login-view {
    .form-row {
      width: 100%;
      height: 52px;
      display: flex;
      justify-content: start;
      align-items: center;
      border-bottom: 1px solid #eee;

      &.form-row-err {
        border-color: #eb5550;
      }
    }

    .form-err-tips {
      padding-top: 6px;
      color: #eb5550;
      font-size: 12px;
    }

    .wb-form-input {
      padding: 15px 0;
      font-size: 16px;
      background-color: transparent;
    }

    .first-sign-tips {
      font-size: 13px;
      color: #999;
      padding-top: 16px;
    }

    .submit-btn {
      padding-top: 16px;
      padding-bottom: 24px;
      width: 100%;
      max-width: 295px;
      margin: 0 auto;
    }

    .read-and-agree {
      padding-top: 66px;
      display: flex;
      justify-content: center;

      .doc-link {
        font-size: 13px;
        color: #1890ff;
        margin: 0 2px;
      }

      .van-checkbox__label {
        font-size: 13px;
        color: #666666;
      }
    }
  }
</style>
